// Account → Store tab (company stores S2, CEO 2026-10-12): this client's
// company store(s), each linking to the store editor. Reads
// GET /company-stores?clientId= (S1). Pattern: ClientSubAccountsTab.
import { Link, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { api, apiErrorMessage } from "@/lib/api";
import { NOT_SET_UP_COPY, STATUS_LABEL, STATUS_TONE, isNotSetUp, shortDate, type StoreListRow } from "@/lib/companyStores";
import type { ClientDetailContext } from "@/pages/ClientDetailPage";
import { STORE_EDITOR_ROLES } from "@/pages/CompanyStoresListPage";

export function ClientStoreTab() {
  const { clientId, client } = useOutletContext<ClientDetailContext>();
  const { user } = useAuth();
  const canEdit = STORE_EDITOR_ROLES.some((r) => user?.roles.includes(r));
  const q = useQuery({
    queryKey: ["company-stores", "list", { clientId }],
    queryFn: () => api<{ stores: StoreListRow[] }>(`/company-stores?clientId=${encodeURIComponent(clientId)}`),
    retry: (n, e) => !isNotSetUp(e) && n < 2,
  });
  const stores = q.data?.stores ?? [];

  if (isNotSetUp(q.error)) {
    return <p role="status" className="rounded-md border border-rule bg-canvas px-4 py-3 text-sm text-ink">{NOT_SET_UP_COPY}</p>;
  }
  return (
    <section aria-label="Company store" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-sm text-muted">An online store where this client's people order merch with their logo.</p>
        {canEdit ? (
          <Button asChild variant="outline" size="sm">
            <Link to={`/sales/company-stores/new?${new URLSearchParams({ clientId, clientName: client.name })}`}>
              <Plus className="h-4 w-4" aria-hidden /> New store
            </Link>
          </Button>
        ) : null}
      </div>
      {q.isLoading ? (
        <div className="h-16 animate-pulse rounded-md bg-white" aria-label="Loading stores" />
      ) : q.isError ? (
        <p role="alert" className="text-sm text-risk">{apiErrorMessage(q.error, "We couldn't load this client's store.")}</p>
      ) : stores.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule px-4 py-6 text-center text-sm text-muted">This client has no company store yet.</p>
      ) : (
        <ul className="divide-y divide-rule rounded-md border border-rule bg-white">
          {stores.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <Link to={`/sales/company-stores/${s.id}`} className="font-medium text-ink hover:underline">{s.name}</Link>
                <p className="text-[13px] text-muted">
                  {s.productCount} product{s.productCount === 1 ? "" : "s"} · {s.memberCount} {s.memberCount === 1 ? "person" : "people"} · created {shortDate(s.createdAt)}
                </p>
              </div>
              <StatusPill tone={STATUS_TONE[s.status]} className="normal-case">{STATUS_LABEL[s.status]}</StatusPill>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
